import { BUILTIN_GESTURES } from "./builtinMotions.generated";

/**
 * Gesture registry: built-in clips merged with a deployment's own.
 *
 * Built-in gestures are original clips generated by
 * scripts/generate-motions.mjs (see that file and public/gestures/README.md).
 * They target the VRM normalized humanoid, so they work on every avatar in
 * Seiza — uploaded VRMs, deployer presets, and designed avatars — with no
 * per-avatar rigging.
 *
 * A deployment adds its own `.vrma` clips through `customGestures` in
 * world.json:
 *   - a custom gesture whose `name` matches a built-in REPLACES it (same
 *     slot in the list, marked as custom in the UI);
 *   - any other name is ADDED alongside the built-ins.
 * Every client reads the same world.json, so a custom gesture resolves to
 * the same clip for everyone in the room.
 */

export interface CustomGestureConfig {
  name: string;
  label: string;
  /** Same-origin path (e.g. /gestures/custom/bow.vrma) or https URL. */
  url: string;
  /** True if the clip drives the legs too (sitting, kneeling, dancing).
   *  Upper-body clips layer over walking; full-body clips replace it. */
  fullBody?: boolean;
  /** True if the clip should stop on its final pose and stay there until
   *  released, instead of playing once. Use it for a raised hand or any
   *  other posture a player holds. */
  hold?: boolean;
  /** How the avatar should meet the ground while this clip holds.
   *  See GestureDefinition.ground — the same values apply. */
  ground?: GroundContact;
  /** Face to wear while this gesture plays. See FACE_EXPRESSIONS. */
  expression?: string;
  /** Kept out of the gesture menu and number keys, for a clip a world's
   *  own code plays. */
  hidden?: boolean;
  /** With `hold`: keeps looping while held (a dance) instead of stopping
   *  on its last frame. */
  loop?: boolean;
}

/**
 * Where a held pose touches the world.
 *
 * A .vrma clip can only bake one hip height, and that height is only correct
 * for the proportions it was authored against: VRM playback scales the hips
 * translation by each avatar's hips height, but leg length varies
 * independently of it, so a kneeling pose baked for one skeleton floats on a
 * long-legged avatar and sinks into the floor on a short-legged one. Rather
 * than guess, clips declare what they are resting on and the app settles the
 * avatar onto it at runtime (see AvatarAnimator).
 *
 *   "none"  — ordinary clip, the avatar stands on its own feet.
 *   "floor" — the legs rest on the ground (kneeling, seiza, sitting down).
 *   "seat"  — the hips rest on a chair or bench at SEAT_HEIGHT.
 *   "body"  — whatever part of the body is lowest rests on the surface
 *             (lying down on a bed).
 */
export type GroundContact = "none" | "floor" | "seat" | "body";

export interface GestureDefinition {
  name: string;
  label: string;
  url: string;
  fullBody: boolean;
  /** Holds its final pose until released (see CustomGestureConfig.hold). */
  hold: boolean;
  /** Not offered in the menu or on the number keys — something else in the
   *  app triggers it. `sit` is the built-in example: chairs trigger it. */
  hidden: boolean;
  /** What this pose rests on, settled per avatar at runtime. */
  ground: GroundContact;
  /** Known up front for built-ins; custom clips report theirs on load. */
  duration: number | null;
  source: "builtin" | "custom";
  /** True when this custom clip replaced a built-in of the same name. */
  overridesBuiltin: boolean;
  /** Expression to wear while this gesture plays, or null for none. */
  expression: string | null;
  /** A held clip that keeps looping (a dance) rather than freezing on its
   *  last frame. */
  loop: boolean;
}

/**
 * Expression names this app will drive.
 *
 * These are three-vrm's own preset names. An uploaded VRM 0.x file uses
 * different labels internally ("joy", "sorrow", "fun"), but three-vrm
 * normalizes those to the VRM 1.0 names at load time, so one set of names
 * covers both — and designer avatars register exactly these too (see
 * designerExpressions.ts). Anything not in this list is rejected rather
 * than passed through, since a typo'd name would otherwise fail silently
 * on every avatar in the room.
 */
export const FACE_EXPRESSIONS = ["neutral", "happy", "sad", "angry", "surprised", "relaxed"] as const;

export function isValidExpression(name: unknown): name is string {
  return typeof name === "string" && (FACE_EXPRESSIONS as readonly string[]).includes(name);
}

/**
 * Which face goes with each built-in gesture.
 *
 * Kept here rather than in builtinMotions.generated.ts on purpose: that
 * file is regenerated wholesale by `npm run generate:motions`, and a hand
 * edit to it would be silently lost the next time anyone ran that script.
 * The generator produces bone rotations; which face suits the result is an
 * authored judgement, so it lives with the other authored gesture data.
 */
const BUILTIN_GESTURE_EXPRESSIONS: Record<string, string> = {
  wave: "happy",
  cheer: "happy",
  clap: "happy",
  nod: "relaxed",
  bow: "relaxed",
  gassho: "relaxed",
  seiza: "relaxed",
  sit: "relaxed",
  // Eyes eased shut: lying in a bed reads as asleep (the same "sleep"
  // face seiza dozes off with; see useFacialExpressions).
  lie: "sleep",
  shrug: "sad",
  // Narrowed eyes and a slight set to the mouth: concentrating and
  // keeping quiet, which is what a crouch is for.
  crouch: "relaxed",
  // `point` and `raisehand` stay neutral: both are about directing
  // attention somewhere else, and a face on them reads as editorial.
};

/**
 * When a gesture makes a sound, in seconds from the start of its clip.
 *
 * Taken from the clip's own keyframes rather than guessed: clap brings
 * the palms together at 0.55s, 0.9s and 1.25s (see the `clap` entry in
 * scripts/generate-motions.mjs), so the sound fires three times, on the
 * contacts. A single sound at the start of a three-clap animation is
 * what it replaced, and it read as the audio being broken rather than
 * merely sparse.
 *
 * Only clap is listed. `cheer` raises both arms and has no impact in it
 * at all, so borrowing the clap sound for it was simply wrong.
 */
export const GESTURE_SOUND_TIMES: Record<string, readonly number[]> = {
  clap: [0.55, 0.9, 1.25],
};

const NAME = /^[a-z0-9][a-z0-9_-]{0,31}$/;
const isAllowedUrl = (url: string) => (url.startsWith("/") && !url.startsWith("//")) || /^https:\/\//i.test(url);

export function buildGestureRegistry(options: {
  enabledGestures?: string[];
  customGestures?: CustomGestureConfig[];
}): GestureDefinition[] {
  const enabled = options.enabledGestures?.length ? new Set(options.enabledGestures) : null;

  const list: GestureDefinition[] = BUILTIN_GESTURES.filter((g) => !enabled || enabled.has(g.name)).map((g) => ({
    name: g.name,
    label: g.label,
    url: g.url,
    fullBody: g.fullBody,
    hold: g.hold,
    hidden: g.hidden,
    ground: g.ground,
    duration: g.duration,
    source: "builtin",
    overridesBuiltin: false,
    expression: BUILTIN_GESTURE_EXPRESSIONS[g.name] ?? null,
    loop: g.loop,
  }));

  for (const custom of options.customGestures ?? []) {
    if (!custom || typeof custom.name !== "string" || !NAME.test(custom.name)) {
      console.warn("Ignoring custom gesture with an invalid name (use lowercase letters, digits, - or _):", custom);
      continue;
    }
    if (typeof custom.url !== "string" || !isAllowedUrl(custom.url)) {
      console.warn(`Ignoring custom gesture "${custom.name}": url must be a same-origin path or https URL.`);
      continue;
    }
    const definition: GestureDefinition = {
      name: custom.name,
      label: typeof custom.label === "string" && custom.label.trim() ? custom.label.trim().slice(0, 40) : custom.name,
      url: custom.url,
      fullBody: custom.fullBody === true,
      hold: custom.hold === true,
      // A custom clip replacing a hidden built-in (`sit`) stays hidden, so
      // a deployment can supply its own sitting pose without it showing up
      // in the menu as a gesture.
      hidden: custom.hidden === true || BUILTIN_GESTURES.some((g) => g.name === custom.name && g.hidden),
      // A replacement clip inherits the built-in's contact by default, so a
      // deployment swapping in its own sitting pose still lands on the chair.
      ground:
        custom.ground ?? BUILTIN_GESTURES.find((g) => g.name === custom.name)?.ground ?? "none",
      duration: null,
      source: "custom",
      overridesBuiltin: false,
      // Same inheritance rule as `ground`: a clip replacing a built-in
      // keeps that built-in's face unless it names its own.
      expression: isValidExpression(custom.expression)
        ? custom.expression
        : (BUILTIN_GESTURE_EXPRESSIONS[custom.name] ?? null),
      loop: custom.loop === true || BUILTIN_GESTURES.some((g) => g.name === custom.name && g.loop),
    };
    const existing = list.findIndex((g) => g.name === custom.name);
    if (existing >= 0) {
      list[existing] = { ...definition, overridesBuiltin: list[existing].source === "builtin" };
    } else {
      list.push(definition);
    }
  }

  return list;
}

export function isValidGestureName(name: unknown): name is string {
  return typeof name === "string" && NAME.test(name);
}

/** The clip a seated avatar holds. Named `sit` so a deployment can replace
 *  it through `customGestures` like any other gesture. */
export const SIT_GESTURE_NAME = "sit";

/** The clip held lying on a bed (a seat whose pose is `lie`). Hidden like
 *  `sit`, and replaceable the same way. */
export const LIE_GESTURE_NAME = "lie";

/** The low, slow stance. A held upper-body clip, so it layers over
 *  walking rather than replacing it (see scripts/generate-motions.mjs). */
export const CROUCH_GESTURE_NAME = "crouch";

/** The kneeling pose the project is named for. Toggled by its number key
 *  and, on touch, by its own button beside the D-pad. */
export const SEIZA_GESTURE_NAME = "seiza";

export function findGesture(gestures: GestureDefinition[], name: string | null): GestureDefinition | null {
  if (!name) return null;
  return gestures.find((g) => g.name === name) ?? null;
}

